<template>
  <div class="cart-page">
    <AppNavbar />

    <main class="container cart-content">
      <div class="cart-head">
        <div class="cart-head-left">
          <h2 class="section-title">Your Cart</h2>
          <span class="count-pill"
            >{{ cartStore.totalCount }}
            {{ cartStore.totalCount === 1 ? "item" : "items" }}</span
          >
        </div>
      </div>

      <section class="cart-card">
        <div v-if="!cartStore.items.length" class="empty-cart">
          <i class="ri-shopping-cart-2-line empty-icon"></i>
          <p class="empty-title">Your cart is empty</p>
          <p class="empty-sub">
            Add some products from the shop to get started.
          </p>
          <button
            type="button"
            class="checkout-btn"
            @click="handleContinueShopping"
          >
            Browse Products
          </button>
        </div>

        <div v-else class="cart-list">
          <!-- DESKTOP HEADER -->
          <div class="cart-grid-header">
            <span>Product</span>
            <span></span>
            <span>Price</span>
            <span>Quantity</span>
            <!-- <span class="align-right">Total</span> -->
          </div>

          <!-- CART ITEMS -->
          <article
            v-for="(item, index) in cartStore.items"
            :key="item.key || index"
            class="cart-item"
            :class="{
              swiping: swipingKey === item.key,
              removing: removingKey === item.key,
            }"
            :style="
              swipingKey === item.key
                ? { transform: `translateX(${swipeOffset}px)` }
                : {}
            "
            @mousedown="startSwipe($event, item.key)"
            @touchstart.passive="startSwipe($event, item.key)"
            @mousemove="onSwipeMove($event)"
            @touchmove.passive="onSwipeMove($event)"
            @mouseup="endSwipe(item.key)"
            @touchend="endSwipe(item.key)"
            @mouseleave="cancelSwipe"
          >
            <!-- DELETE HINT (revealed on swipe) -->
            <div
              class="swipe-delete-hint"
              :class="{ visible: swipingKey === item.key && swipeOffset < -40 }"
            >
              <i class="ri-delete-bin-line"></i>
              <span>Remove</span>
            </div>

            <div class="cart-item-image-wrap">
              <img
                v-if="item.image"
                :src="item.image"
                :alt="item.name || 'Product Image'"
                class="cart-item-image"
                @error="handleImageLoadError(item)"
              />
              <div v-else class="no-image">
                <i class="ri-image-line"></i>
              </div>
            </div>

            <div class="cart-item-info">
              <h3 class="item-title">{{ item.name }}</h3>

              <button
                type="button"
                class="desktop-remove-btn"
                @click="handleRemove(item.key)"
              >
                <i class="ri-delete-bin-line"></i>
                Remove
              </button>

              <p
                class="item-coin-mobile"
                v-if="
                  showCv &&
                  item.coinValue !== undefined &&
                  item.coinValue !== null
                "
              >
                CV: {{ formatNumber(item.coinValue) }}
              </p>
              <p
                class="item-coin-mobile"
                v-if="Number(item.shippingCharge) > 0"
              >
                Shipping: {{ formatRs(item.shippingCharge) }}
              </p>

              <p class="item-price-mobile">
                <span
                  v-if="comboInfoByKey[item.key]"
                  class="item-price-original"
                >
                  {{ formatRs(comboInfoByKey[item.key].originalPrice) }}
                </span>
                <span>{{ formatRs(getDisplayPrice(item)) }}</span>
              </p>

              <!-- Combo discount (mobile) -->
              <div
                v-if="comboInfoByKey[item.key]"
                class="combo-discount combo-discount-mobile"
              >
                <div
                  v-for="entry in comboInfoByKey[item.key].entries"
                  :key="entry.discountId"
                  class="combo-discount-tag"
                >
                  <i class="ri-price-tag-3-fill"></i>
                  <span
                    >{{ entry.name }} - {{ formatRs(entry.amount) }} Off!</span
                  >
                </div>
              </div>
            </div>

            <div class="item-price">
              <span style="padding-right: 6px">{{
                formatRs(getDisplayPrice(item))
              }}</span>
              <span v-if="comboInfoByKey[item.key]" class="item-price-original">
                {{ formatRs(comboInfoByKey[item.key].originalPrice) }}
              </span>

              <!-- Combo discount (desktop) -->
              <div v-if="comboInfoByKey[item.key]" class="combo-discount">
                <div
                  v-for="entry in comboInfoByKey[item.key].entries"
                  :key="entry.discountId"
                  class="combo-discount-tag"
                >
                  <i class="ri-price-tag-3-fill"></i>
                  <span
                    >{{ entry.name }} - {{ formatRs(entry.amount) }} Off</span
                  >
                </div>
              </div>

              <div
                class="item-coin"
                v-if="
                  showCv &&
                  item.coinValue !== undefined &&
                  item.coinValue !== null
                "
              >
                CV: {{ formatNumber(item.coinValue) }}
              </div>
              <div class="item-coin" v-if="Number(item.shippingCharge) > 0">
                Shipping: {{ formatRs(item.shippingCharge) }}
              </div>
            </div>

            <div class="item-qty-controls">
              <button
                type="button"
                class="qty-btn"
                @click="handleDecrease(item.key)"
              >
                <i class="ri-subtract-line"></i>
              </button>
              <span class="qty-value">{{ item.quantity || 1 }}</span>
              <button
                type="button"
                class="qty-btn"
                @click="handleIncrease(item.key)"
              >
                <i class="ri-add-line"></i>
              </button>
            </div>

            <!-- <div class="item-total align-right">
              {{ formatRs(cartStore.getLineTotal(item)) }}
            </div> -->
          </article>

          <!-- SUMMARY -->
          <div class="cart-summary-block">
            <div class="summary-row">
              <span class="summary-label">Gross Total</span>
              <span class="summary-value">{{
                formatRs(grossTotalAmount)
              }}</span>
            </div>
            <div
              v-for="combo in comboSummary.combos"
              :key="combo.comboId"
              class="summary-row combo-discount-row"
            >
              <span class="summary-label">
                <i class="ri-price-tag-3-fill"></i>
                Combo Discount &middot; {{ combo.name }}
              </span>
              <span class="summary-value">-{{ formatRs(combo.amount) }}</span>
            </div>
            <div class="summary-row">
              <span class="summary-label">Subtotal</span>
              <span class="summary-value">{{ formatRs(subtotalAmount) }}</span>
            </div>
            <div class="summary-row">
              <span class="summary-label">Shipping</span>
              <span class="summary-value">{{
                formatRs(cartStore.orderShippingCharge)
              }}</span>
            </div>
            <div class="summary-row" v-if="showCv">
              <span class="summary-label">Total CV</span>
              <span class="summary-value">{{
                formatNumber(cartStore.grandTotalCoinValue)
              }}</span>
            </div>
            <div class="summary-row delivery-row">
              <span class="delivery-estimate">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                >
                  <rect x="1" y="3" width="15" height="13"></rect>
                  <polygon points="16 8 20 8 23 11 23 16 16 16 16 8"></polygon>
                  <circle cx="5.5" cy="18.5" r="2.5"></circle>
                  <circle cx="18.5" cy="18.5" r="2.5"></circle>
                </svg>
                Estimated Delivery: <strong>25 - 30 Days</strong>
              </span>
            </div>
            <div class="summary-divider"></div>

            <div class="summary-divider"></div>
            <div class="summary-row total-row">
              <span class="summary-label total-label"
                >Order Total (Incl. of taxes)</span
              >
              <strong class="summary-total">{{
                formatRs(orderTotalAmount)
              }}</strong>
            </div>
          </div>

          <div class="cart-actions">
            <button
              type="button"
              class="continue-btn"
              @click="handleContinueShopping"
            >
              <i class="ri-arrow-left-line"></i> Continue Shopping
            </button>
            <button type="button" class="checkout-btn" @click="handleCheckout">
              Proceed to Checkout <i class="ri-arrow-right-line"></i>
            </button>
          </div>
        </div>
      </section>
    </main>

    <Footer />
  </div>
</template>

<script setup>
import { computed, onMounted, ref } from "vue";
import { useRouter } from "vue-router";
import { getToken } from "rdep-ecom-sdk";
import { getItemComboInfo, getCartComboSummary } from "@/data/comboProducts.js";
import { useCartStore } from "../../stores/useCartStore.js";
import AppNavbar from "../AppNavbar.vue";
import Footer from "../FooterView.vue";

const router = useRouter();
const cartStore = useCartStore();

function normalizeRoleToken(value) {
  return String(value || "")
    .trim()
    .toUpperCase()
    .replace(/[^A-Z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");
}

const userRoleToken = computed(() => {
  try {
    const rawAuth = window.localStorage.getItem("auth.currentUser");
    const authPayload = rawAuth ? JSON.parse(rawAuth) : {};
    const user = authPayload?.data || authPayload || {};
    const roleCandidates = [
      user?.userType,
      user?.role,
      user?.loginRole,
      user?.customerType,
    ]
      .map(normalizeRoleToken)
      .filter(Boolean);

    return roleCandidates[0] || "";
  } catch (error) {
    return "";
  }
});

const showCv = computed(() => userRoleToken.value === "DISTRIBUTOR");

const orderTotalAmount = computed(
  () => Number(cartStore.displayOrderTotal) || 0,
);

// --- COMBO DISCOUNTS (Combo Shop only) ---
// Only lines carrying a COMBINATION_POOL discount that matches a combo in
// comboProducts.js are affected. Regular products return null here, so
// their price / subtotal behaviour is exactly as before.
const comboInfoByKey = computed(() => {
  const map = {};
  cartStore.items.forEach((item) => {
    const info = getItemComboInfo(item);
    if (info) map[item.key] = info;
  });
  return map;
});

const comboSummary = computed(() => getCartComboSummary(cartStore.items));

// The API's netTotal is already after the combo discount.
const subtotalAmount = computed(() => Number(cartStore.orderNetTotal) || 0);

const grossTotalAmount = computed(() => {
  const net = Number(cartStore.orderNetTotal) || 0;
  const discount = comboSummary.value.totalDiscount;
  return discount > 0 ? Math.round((net + discount) * 100) / 100 : net;
});

function getDisplayPrice(item) {
  const info = comboInfoByKey.value[item.key];
  return info ? info.netPrice : item.price;
}

// --- SWIPE TO REMOVE ---
const swipingKey = ref(null);
const removingKey = ref(null);
const swipeOffset = ref(0);
let swipeStartX = 0;
let isSwiping = false;

function getClientX(event) {
  return event.touches ? event.touches[0].clientX : event.clientX;
}

function startSwipe(event, key) {
  if (window.innerWidth > 900) return;

  swipingKey.value = key;
  swipeStartX = getClientX(event);
  swipeOffset.value = 0;
  isSwiping = true;
}

function onSwipeMove(event) {
  if (!isSwiping || !swipingKey.value) return;
  const delta = getClientX(event) - swipeStartX;
  if (delta < 0) {
    swipeOffset.value = Math.max(delta, -120);
  } else {
    swipeOffset.value = 0;
  }
}

async function endSwipe(key) {
  if (!isSwiping) return;
  isSwiping = false;

  if (swipeOffset.value < -80) {
    removingKey.value = key;
    swipeOffset.value = -400;
    await new Promise((r) => setTimeout(r, 300));
    await cartStore.removeItem(key);
    removingKey.value = null;
  } else {
    swipeOffset.value = 0;
  }
  swipingKey.value = null;
}

function cancelSwipe() {
  if (!isSwiping) return;
  isSwiping = false;
  swipeOffset.value = 0;
  swipingKey.value = null;
}

function formatRs(value) {
  if (value === undefined || value === null || value === "") return "";
  const normalizedValue =
    typeof value === "string" ? value.replace(/,/g, "").trim() : value;
  if (normalizedValue === "") return "";
  const parsedNumber = Number(normalizedValue);
  if (!Number.isFinite(parsedNumber)) return `₹${value}`;
  const formattedValue = new Intl.NumberFormat("en-IN", {
    maximumFractionDigits: 20,
  }).format(parsedNumber);
  return `₹${formattedValue}`;
}

function formatNumber(value) {
  if (value === undefined || value === null || value === "") return "0";
  const normalizedValue =
    typeof value === "string" ? value.replace(/,/g, "").trim() : value;
  if (normalizedValue === "") return "0";
  const parsedNumber = Number(normalizedValue);
  if (!Number.isFinite(parsedNumber)) return String(value);
  return new Intl.NumberFormat("en-IN", { maximumFractionDigits: 20 }).format(
    parsedNumber,
  );
}

onMounted(() => {
  cartStore.initializeCount();
  cartStore.refreshCartData();
});

async function handleIncrease(itemKey) {
  await cartStore.increaseItemQuantity(itemKey);
}
async function handleDecrease(itemKey) {
  await cartStore.decreaseItemQuantity(itemKey);
}
async function handleRemove(itemKey) {
  await cartStore.removeItem(itemKey);
}

async function handleCheckout() {
  const authToken = String(getToken() || "").trim();
  if (!authToken) {
    await router.push({
      name: "login",
      query: {
        redirect: "/cart",
      },
    });
    return;
  }

  await cartStore.refreshCartData();
  await router.push({
    name: "place-order",
  });
}

async function handleContinueShopping() {
  await cartStore.refreshCartData();
  await router.push({ name: "shop" });
}

function handleImageLoadError(item) {
  if (!item || !item.key) return;
  const index = cartStore.items.findIndex((row) => row.key === item.key);
  if (index === -1) return;
  cartStore.items[index] = { ...cartStore.items[index], image: "" };
}
</script>

<style scoped>
@import url("https://cdn.jsdelivr.net/npm/remixicon@4.2.0/fonts/remixicon.css");

.cart-page {
  background: #f8fafc;
  min-height: 100vh;
}

.container {
  width: 90%;
  max-width: 1200px;
  margin: 0 auto;
}

.cart-content {
  padding: 36px 0 60px;
}

/* HEAD */
.cart-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 24px;
  flex-wrap: wrap;
  gap: 12px;
}
.cart-head-left {
  display: flex;
  align-items: center;
  gap: 12px;
}
.section-title {
  margin: 0;
  color: #0a2240;
  font-size: 24px;
  font-weight: 800;
}
.count-pill {
  background: rgba(0, 61, 165, 0.08);
  color: #003da5;
  border-radius: 999px;
  padding: 4px 12px;
  font-weight: 700;
  font-size: 13px;
}
.continue-btn-top {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  border: 1px solid rgba(0, 61, 165, 0.2);
  background: #fff;
  color: #003da5;
  border-radius: 8px;
  padding: 8px 16px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
}
.continue-btn-top:hover {
  background: #003da5;
  color: #fff;
}

/* CARD */
.cart-card {
  background: #ffffff;
  border: 1px solid rgba(0, 61, 165, 0.08);
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 4px 24px rgba(0, 61, 165, 0.04);
}

/* EMPTY STATE */
.empty-cart {
  padding: 80px 24px;
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
}
.empty-icon {
  font-size: 56px;
  color: rgba(0, 61, 165, 0.15);
}
.empty-title {
  font-size: 20px;
  font-weight: 700;
  color: #0a2240;
  margin: 0;
}
.empty-sub {
  font-size: 14px;
  color: #5f6368;
  margin: 0 0 8px;
}

/* CART LIST */
.cart-list {
  display: flex;
  flex-direction: column;
}

/* DESKTOP HEADER */
.cart-grid-header {
  display: grid;
  grid-template-columns: 100px 1fr 160px 160px 140px;
  gap: 16px;
  padding: 14px 28px;
  background: #f8fafc;
  border-bottom: 1px solid rgba(0, 61, 165, 0.06);
  color: #5f6368;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.6px;
}
.cart-grid-header span:nth-child(4) {
  grid-column: 5;
  justify-self: center;
  text-align: right;
  padding-left: 14px;
}
.align-right {
  text-align: right;
}

/* CART ITEM */
.cart-item {
  position: relative;
  display: grid;
  grid-template-columns: 100px 1fr 160px 160px 140px;
  gap: 16px;
  align-items: center;
  padding: 20px 28px;
  border-bottom: 1px solid rgba(0, 61, 165, 0.06);
  transition: transform 0.15s ease, background 0.2s;
  cursor: default;
  user-select: none;
  overflow: hidden;
  background: #fff;
}
.cart-item:last-of-type {
  border-bottom: none;
}
.cart-item:hover {
  background: #fafcff;
}
.cart-item.swiping {
  transition: none;
  cursor: grabbing;
}
.cart-item.removing {
  transition: transform 0.3s ease, opacity 0.3s ease;
  opacity: 0;
}

.delivery-row {
  margin-top: 2px;
}

.delivery-estimate {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  font-size: 13px;
  color: #5f6368;
}

.delivery-estimate svg {
  flex-shrink: 0;
  color: #003da5;
}

.delivery-estimate strong {
  color: #0a2240;
  font-weight: 700;
}

/* SWIPE DELETE HINT */
.swipe-delete-hint {
  position: absolute;
  right: 0;
  top: 0;
  bottom: 0;
  width: 90px;
  background: #fee2e2;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 4px;
  color: #b91c1c;
  font-size: 12px;
  font-weight: 700;
  opacity: 0;
  transition: opacity 0.15s ease;
  pointer-events: none;
  z-index: 0;
}
.swipe-delete-hint i {
  font-size: 20px;
}
.swipe-delete-hint.visible {
  opacity: 1;
}

@media (max-width: 900px) {
  .swipe-delete-hint {
    width: 90px;
  }
}

/* IMAGE */
.cart-item-image-wrap {
  width: 90px;
  height: 90px;
  border: 1px solid rgba(0, 61, 165, 0.08);
  border-radius: 10px;
  overflow: hidden;
  background: #fafafa;
  flex-shrink: 0;
  position: relative;
  z-index: 1;
}
.cart-item-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
}
.no-image {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #c0cce0;
  font-size: 24px;
}

.desktop-remove-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin-top: 8px;
  padding: 0;
  border: none;
  background: transparent;
  color: #dc2626;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
}

.desktop-remove-btn:hover {
  color: #b91c1c;
}

.item-action {
  display: flex;
  justify-content: center;
}

/* ITEM INFO */
.cart-item-info {
  display: flex;
  flex-direction: column;
  gap: 4px;
  position: relative;
  z-index: 1;
}
.item-title {
  margin: 0;
  color: #0a2240;
  font-size: 15px;
  font-weight: 700;
  line-height: 1.4;
}
.item-price-mobile {
  display: none;
  font-size: 15px;
  font-weight: 700;
  color: #003da5;
  margin: 0;
  align-items: baseline;
  gap: 7px;
}

.item-coin-mobile {
  display: none;
  font-size: 13px;
  font-weight: 600;
  color: #0a2240;
  margin: 0;
}

/* PRICE */
.item-price {
  color: #0a2240;
  font-size: 14px;
  font-weight: 600;
  position: relative;
  z-index: 1;
}
.item-price-original {
  color: #94a3b8;
  font-size: 12px;
  font-weight: 500;
  text-decoration: line-through;
  text-decoration-thickness: 1px;
}

.item-coin {
  color: #0a2240;
  font-size: 13px;
  font-weight: 600;
  position: relative;
  z-index: 1;
}

/* QTY */
.item-qty-controls {
  display: inline-flex;
  align-items: center;
  border: 1.5px solid rgba(0, 61, 165, 0.2);
  border-radius: 50px;
  overflow: hidden;
  background: #fff;
  width: fit-content;
  position: relative;
  z-index: 1;
  grid-column: 5;
  justify-self: end;
}
.qty-btn {
  width: 34px;
  height: 34px;
  border: none;
  background: transparent;
  cursor: pointer;
  color: #003da5;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  transition: background 0.15s;
}
.qty-btn:hover {
  background: rgba(0, 61, 165, 0.06);
}
.qty-value {
  min-width: 36px;
  text-align: center;
  font-size: 14px;
  font-weight: 700;
  color: #0a2240;
  border-left: 1px solid rgba(0, 61, 165, 0.1);
  border-right: 1px solid rgba(0, 61, 165, 0.1);
  line-height: 34px;
}

/* ITEM TOTAL */
.item-total {
  color: #003da5;
  font-size: 15px;
  font-weight: 800;
  position: relative;
  z-index: 1;
}

/* COMBO DISCOUNT */
.combo-discount {
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin: 6px 0 4px;
}
.combo-discount-tag {
  display: flex;
  align-items: flex-start;
  gap: 6px;
  color: #15803d;
  font-size: 13px;
  font-weight: 700;
  line-height: 1.35;
}
.item-price .combo-discount-tag {
  white-space: nowrap;
}
.combo-discount-tag i {
  flex-shrink: 0;
  margin-top: 1px;
  font-size: 14px;
}
.combo-net {
  color: #5f6368;
  font-size: 12px;
  font-weight: 600;
}
.combo-discount-mobile {
  display: none;
}
.combo-discount-row .summary-label,
.combo-discount-row .summary-value {
  color: #15803d;
  font-weight: 600;
}
.combo-discount-row .summary-label i {
  margin-right: 4px;
}

/* SWIPE HINT TEXT */
.swipe-hint {
  font-size: 12px;
  color: #9aabb8;
  text-align: center;
  padding: 10px 0 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 5px;
}
.swipe-hint i {
  font-size: 14px;
}

/* SUMMARY */
.cart-summary-block {
  padding: 24px 28px 20px;
  border-top: 1px solid rgba(0, 61, 165, 0.06);
  display: flex;
  flex-direction: column;
  gap: 10px;
  background: #fafcff;
}
.summary-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
}
.summary-label {
  font-size: 14px;
  color: #5f6368;
  font-weight: 500;
}
.summary-label.muted {
  font-size: 12px;
  color: #9aabb8;
}
.summary-value {
  font-size: 14px;
  font-weight: 600;
  color: #0a2240;
}
.summary-divider {
  height: 1px;
  background: rgba(0, 61, 165, 0.08);
  margin: 4px 0;
}
.total-row {
  margin-top: 4px;
}
.total-label {
  font-size: 16px;
  font-weight: 700;
  color: #0a2240;
}
.summary-total {
  font-size: 22px;
  font-weight: 800;
  color: #003da5;
}

/* ACTIONS */
.cart-actions {
  padding: 20px 28px 24px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  background: #fff;
  border-top: 1px solid rgba(0, 61, 165, 0.06);
}
.continue-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  border: 1px solid rgba(0, 61, 165, 0.2);
  background: #fff;
  color: #003da5;
  border-radius: 50px;
  padding: 11px 22px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
}
.continue-btn:hover {
  background: rgba(0, 61, 165, 0.05);
}
.checkout-btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: #ffc72c;
  border: 2px solid #ffc72c;
  color: #0a2240;
  border-radius: 50px;
  padding: 11px 28px;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
}
.checkout-btn:hover {
  background: #003da5;
  border-color: #003da5;
  color: #fff;
  box-shadow: 0 4px 16px rgba(0, 61, 165, 0.3);
}

/* RESPONSIVE */
@media (max-width: 900px) {
  .cart-grid-header {
    display: none;
  }
  .continue-btn-top {
    display: none;
  }

  .cart-item {
    grid-template-columns: 90px 1fr;
    grid-template-rows: auto auto;
    gap: 12px;
    padding: 16px 20px;
  }

  .item-price {
    display: none;
  }
  .item-coin {
    display: none;
  }
  .item-price-mobile {
    display: inline-flex;
  }
  .item-coin-mobile {
    display: block;
  }
  .combo-discount-mobile {
    display: flex;
  }
  .cart-item {
    cursor: grab;
  }

  .item-total {
    padding-right: 12px;
  }

  .desktop-remove-btn {
    display: none;
  }

  .item-qty-controls {
    grid-column: 1;
    grid-row: 2;
    justify-self: start;
  }

  .item-total {
    grid-column: 2;
    grid-row: 2;
    text-align: right;
    align-self: center;
  }

  .cart-summary-block {
    padding: 20px;
  }
  .cart-actions {
    padding: 16px 20px 20px;
    flex-direction: column;
  }
  .continue-btn,
  .checkout-btn {
    width: 100%;
    justify-content: center;
  }
  .summary-total {
    font-size: 20px;
  }
}

@media (max-width: 600px) {
  .cart-content {
    padding: 24px 0 40px;
  }
  .section-title {
    font-size: 20px;
  }
  .cart-item {
    padding: 14px 16px;
  }
  .cart-summary-block {
    padding: 16px;
  }
  .cart-actions {
    padding: 14px 16px 18px;
  }
}
</style>
